import { Api, Fixtuur } from '@fixtuur/backpack';
import { Ui } from '@dora/vivian';
import * as Hooks from 'preact/hooks';
import { useSelector } from '../store/redux';
import * as Selectors from '../store/selectors';

/**
 * Responsible for generating the properties that will be shown for the 'hero' product in the furniture being configured
 * Currently this is only the properties of the hero part itself, but in future may include additional properties
 */

export const useGetBuildPropertyOptions = (
    heroPart: Fixtuur.Part.Id | null,
    inspirationProductGroup: Fixtuur.InspirationProduct.InspirationProductGroup | null,
): {
    selectableBuildPropertiesLoading: boolean;
    selectableBuildProperties: Ui.PropertyOption.PropertyChoice[];
} => {
    const inConfiguration = useSelector(Selectors.Initialisation.inConfiguration);

    const configurationPropertyOptions = useGetBuildPropertyConfigurationOptions(heroPart);
    const inspirationPropertyOptions = useGetBuildPropertyProductOptions(inspirationProductGroup);

    return inConfiguration ? configurationPropertyOptions : inspirationPropertyOptions;
};

export const useGetBuildPropertyProductOptions = (
    inspirationProductGroup: Fixtuur.InspirationProduct.InspirationProductGroup | null,
): {
    selectableBuildPropertiesLoading: boolean;
    selectableBuildProperties: Ui.PropertyOption.PropertyChoice[];
} => {
    const currentProductId = useSelector(Selectors.Initialisation.currentInspirationProductId);

    const selectableBuildProperties = Hooks.useMemo(
        () =>
            currentProductId && inspirationProductGroup
                ? Ui.PropertyOption.fromFixtuurInspirationProduct(
                      inspirationProductGroup,
                      currentProductId,
                  )
                : [],
        [inspirationProductGroup, currentProductId],
    );

    return { selectableBuildPropertiesLoading: false, selectableBuildProperties };
};

export const useGetBuildPropertyConfigurationOptions = (
    heroPart: Fixtuur.Part.Id | null,
): {
    selectableBuildPropertiesLoading: boolean;
    selectableBuildProperties: Ui.PropertyOption.PropertyChoice[];
} => {
    const currentConfigurator = useSelector(Selectors.Initialisation.currentConfiguratorDefinition);
    const configurableProperties = useSelector(Selectors.Initialisation.configurableProperties);
    const buildPropertySelections = useSelector(Selectors.Selections.buildPropertySelections);

    const buildOptions = useSelector(Selectors.Options.buildOptions);
    const allOptions = useSelector(Selectors.Options.allOptions);

    const selectableBuildProperties = Hooks.useMemo(() => {
        /**  Handle Configuration */
        const hasOptions = buildOptions !== null && allOptions !== null;
        const inConfigurationAndHeroPartSet = currentConfigurator && heroPart;
        const dependenciesLoaded = configurableProperties && buildPropertySelections;

        if (inConfigurationAndHeroPartSet && dependenciesLoaded && hasOptions) {
            const buildProperties = Api.ConfiguratorDefinition.getBuildProperties(
                currentConfigurator,
                heroPart,
            );
            const buildPropertyOptions = Fixtuur.Configuration.Options.filterToNames(
                buildProperties,
                allOptions,
            );
            const buildPropertyFilters = Ui.PropertyOption.fromFixtuurOptions(
                configurableProperties,
                buildPropertyOptions,
                buildPropertySelections,
                buildOptions,
            );
            const displayOrder = currentConfigurator.configurablePropertiesDisplayOrder;
            return displayOrder
                ? buildPropertyFilters.sort(
                      (a, b) => displayOrder.indexOf(a.name) - displayOrder.indexOf(b.name),
                  )
                : buildPropertyFilters;
        }
        return [];
    }, [allOptions, heroPart, buildPropertySelections, currentConfigurator, buildOptions]);

    // never loading if we're in Inspiration, as there are no build properties. Otherwise loading if we don't have options yet
    const isLoading = buildOptions === null || allOptions === null;

    return { selectableBuildPropertiesLoading: isLoading, selectableBuildProperties };
};
